import * as React from 'react';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { useTranslation } from 'react-i18next';

import { combineClassNames } from '@utils/combineClassNames';
import Icon from '@common/components/icon';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from '@common/components/ui/combobox';
import { Message } from '../../message';

type Option = Record<string, any>;

export type SelectOptionComponentProps<O extends {} = Option> = {
  children?: React.ReactNode;
  option: O;
  isSelected: boolean;
  isFocused?: boolean;
  isDisabled: boolean;
  className?: string;
  onSelect: (option: O) => void;
  onFocus?: (option: O, event: React.MouseEvent | React.FocusEvent) => void;
};

type SelectValue = {
  label?: string | null;
  value?: string | boolean | number | Record<string, any> | null;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  href?: string;
  target?: string;
  disabled?: boolean;
  __create?: boolean;
  [key: string]: any;
};

export type SelectProps = {
  value?: SelectValue | SelectValue[] | string | boolean | number | null;
  onChange: (newValue: any) => void;
  options?: SelectValue[];
  getOptions?: (value: string) => Promise<SelectValue[]>;
  OptionComponent?: React.ComponentType<SelectOptionComponentProps>;
  optionComponent?: React.ComponentType<SelectOptionComponentProps>;
  valueComponent?: React.ComponentType<{ value: SelectValue }>;
  valueRenderer?: (option: any) => React.ReactNode;
  optionRenderer?: (option: any) => React.ReactNode;
  onInputChange?: (value: string) => string | void;
  className?: string;
  warning?: boolean | string;
  error?: boolean | string;
  simplifyMultiValue?: boolean;
  loadingPlaceholder?: React.ReactNode;
  autoload?: boolean;
  cache?: boolean;
  onNewOptionClick?: (option: SelectValue) => void;
  promptTextCreator?: (label: string) => string;
  getValueChipClassName?: (value: SelectValue) => string | undefined;
  multiValueClearPosition?: 'leading' | 'trailing';
  /** Accepted for existing call sites. The popup is portaled, so this is unused. */
  menuEscape?: boolean;
  menuContainerStyle?: React.CSSProperties;
  arrowRenderer?: (...args: any[]) => React.ReactNode;
  filterOption?: (option: SelectValue, query: string) => boolean;
  placeholder?: React.ReactNode;
  searchable?: boolean;
  multi?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  creatable?: boolean;
  isLoading?: boolean;
  valueKey?: string;
  labelKey?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  id?: string;
  name?: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  [key: string]: any;
};

const CREATE_OPTION_CLASS = 'Select-create-option-placeholder';

function readKey(option: any, key: string) {
  if (option == null || typeof option !== 'object') return option;
  if (Object.prototype.hasOwnProperty.call(option, key)) return option[key];
  return option.value;
}

function optionLabel(option: any, key: string) {
  if (option == null || typeof option !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(option, key)) return option[key];
  return option.label;
}

function readLabelText(option: any, key: string) {
  if (option == null || typeof option !== 'object') return option == null ? '' : String(option);
  const label = optionLabel(option, key);
  if (typeof label === 'string' || typeof label === 'number' || typeof label === 'boolean') {
    return String(label);
  }
  if (typeof option.text === 'string') return option.text;
  return '';
}

function readLabelNode(option: any, key: string): React.ReactNode {
  const label = optionLabel(option, key);
  if (React.isValidElement(label)) return label;
  return readLabelText(option, key);
}

function isCreateOption(option: any) {
  return !!option?.__create || option?.className === CREATE_OPTION_CLASS;
}

function findOption(raw: any, pool: SelectValue[], valueKey: string, labelKey: string) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw as SelectValue;
  const match = pool.find((option) => readKey(option, valueKey) === raw);
  if (match) return match;
  return {
    [valueKey]: raw,
    [labelKey]: String(raw),
    label: String(raw),
    value: raw,
  } as SelectValue;
}

type SelectControlProps = SelectProps & {
  creatable?: boolean;
};

function SelectControl({
  className,
  getOptions,
  OptionComponent,
  optionComponent,
  valueComponent: ValueComponent,
  valueRenderer,
  optionRenderer,
  onInputChange,
  simplifyMultiValue,
  placeholder,
  searchable = false,
  filterOption,
  error,
  warning,
  onNewOptionClick,
  promptTextCreator,
  getValueChipClassName,
  multiValueClearPosition,
  onOpen,
  onClose,
  onFocus,
  onBlur,
  creatable = false,
  clearable = true,
  disabled = false,
  isLoading = false,
  loadingPlaceholder,
  autoload = false,
  multi = false,
  options,
  value,
  onChange,
  valueKey = 'value',
  labelKey = 'label',
  arrowRenderer,
  id,
  name,
  style,
  autoFocus,
}: SelectControlProps) {
  const { t } = useTranslation();
  const hostRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const getOptionsRef = useRef(getOptions);
  const ignoreValueChangeUntil = useRef(0);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [queryDirty, setQueryDirty] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState<SelectValue[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [highlighted, setHighlighted] = useState<SelectValue | undefined>(undefined);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | undefined>(undefined);

  getOptionsRef.current = getOptions;

  const knownOptions = useMemo(
    () => (getOptions ? asyncOptions : options ?? []),
    [asyncOptions, getOptions, options],
  );

  const resolvedSingle = useMemo(
    () => (multi || Array.isArray(value) ? null : findOption(value, knownOptions, valueKey, labelKey)),
    [knownOptions, labelKey, multi, value, valueKey],
  );

  const resolvedMulti = useMemo(() => {
    if (!multi) return [];
    const list = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
    return list
      .map((item) => findOption(item, knownOptions, valueKey, labelKey))
      .filter((item): item is SelectValue => !!item);
  }, [knownOptions, labelKey, multi, value, valueKey]);

  const trimmedQuery = query.trim();
  const hasExactMatch = useMemo(() => {
    if (!trimmedQuery) return false;
    const queryText = trimmedQuery.toLowerCase();
    const matches = (option: SelectValue) => readLabelText(option, labelKey).toLowerCase() === queryText;
    if (knownOptions.some(matches)) return true;
    const selected = multi ? resolvedMulti : (resolvedSingle ? [resolvedSingle] : []);
    return selected.some(matches);
  }, [knownOptions, labelKey, multi, resolvedMulti, resolvedSingle, trimmedQuery]);

  const createItem = useMemo(() => {
    if (!creatable || !trimmedQuery || hasExactMatch) return null;
    const prompt = promptTextCreator
      ? promptTextCreator(trimmedQuery)
      : t('common:select_create_option', { label: trimmedQuery });
    return {
      __create: true,
      label: prompt,
      value: trimmedQuery,
      [labelKey]: prompt,
      [valueKey]: trimmedQuery,
      className: CREATE_OPTION_CLASS,
    } as SelectValue;
  }, [creatable, hasExactMatch, labelKey, promptTextCreator, t, trimmedQuery, valueKey]);

  const items = useMemo(() => {
    const selectedKeys = new Set(resolvedMulti.map((option) => readKey(option, valueKey)));
    const merged = multi
      ? knownOptions.filter((option) => !selectedKeys.has(readKey(option, valueKey)))
      : [...knownOptions];
    if (!multi && resolvedSingle
      && !merged.some((item) => readKey(item, valueKey) === readKey(resolvedSingle, valueKey))) {
      merged.push(resolvedSingle);
    }
    if (createItem) merged.push(createItem);
    return merged;
  }, [createItem, knownOptions, multi, resolvedMulti, resolvedSingle, valueKey]);

  const filteredItems = getOptions
    ? [
      ...asyncOptions.filter((option) => (
        !multi || !resolvedMulti.some((selected) => readKey(selected, valueKey) === readKey(option, valueKey))
      )),
      ...(createItem ? [createItem] : []),
    ]
    : undefined;

  const placeholderNode = placeholder !== undefined
    ? placeholder
    : ((searchable || getOptions) && t('common:select_placeholder_search'))
      || t('common:select_placeholder_select');
  const placeholderText = typeof placeholderNode === 'string' || typeof placeholderNode === 'number'
    ? String(placeholderNode)
    : ((searchable || getOptions)
      ? t('common:select_placeholder_search')
      : t('common:select_placeholder_select'));
  const loadingText = loadingPlaceholder ?? t('common:select_loading_placeholder');

  const clearPosition = multiValueClearPosition
    ?? (getValueChipClassName !== undefined ? 'trailing' : 'leading');
  const hideArrow = typeof arrowRenderer === 'function' && arrowRenderer() == null;
  const OptionRenderer = OptionComponent || optionComponent;
  const hasCustomValue = !!(ValueComponent || valueRenderer);
  const hasValue = multi ? resolvedMulti.length > 0 : !!resolvedSingle;
  const showSelectedFace = Boolean(resolvedSingle) && (!open || !queryDirty);
  const isAsync = !!getOptions;
  const showQueryInput = searchable || isAsync || creatable;
  const queryInputCollapsed = !showQueryInput || (hasValue && !open);
  const itemFilter = useMemo(() => {
    if (isAsync) return null;
    if (!filterOption) return undefined;
    return (item: SelectValue, nextQuery: string) => (
      !!item?.__create || filterOption(item, nextQuery)
    );
  }, [filterOption, isAsync]);

  const fullClassName = combineClassNames('Select', className, {
    'Select--multi': multi,
    'Select--single': !multi,
    'is-open': open,
    'is-disabled': disabled,
    'has-value': hasValue,
    'is-searchable': showQueryInput,
    ReactSelect__MultiSimplified: simplifyMultiValue,
    ReactSelect__MultiClearTrailing: multi && clearPosition === 'trailing',
    ReactSelect__MultiClearLeading: multi && clearPosition === 'leading',
    'Form__control--invalid': !warning && !!error,
    'Form__control--warning': !!warning,
  });

  useEffect(() => {
    const node = hostRef.current;
    if (!open || !node) return undefined;
    const syncSide = () => {
      const popup = node.ownerDocument.querySelector(
        '[data-slot="combobox-content"].Select-menu-outer[data-open]',
      );
      node.classList.toggle('select-open-upward', popup?.getAttribute('data-side') === 'top');
    };
    syncSide();
    const frame = requestAnimationFrame(syncSide);
    return () => {
      cancelAnimationFrame(frame);
      node.classList.remove('select-open-upward');
    };
  }, [open]);

  useEffect(() => {
    const load = getOptionsRef.current;
    if (!load || !open) return undefined;

    if (!autoload && trimmedQuery === '') {
      setAsyncOptions([]);
      setIsFetching(false);
      return undefined;
    }

    let cancelled = false;
    setIsFetching(true);
    load(query)
      .then((result) => {
        if (!cancelled) setAsyncOptions(result || []);
      })
      .catch(() => {
        if (!cancelled) setAsyncOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [autoload, isAsync, open, query, trimmedQuery]);

  const sameOption = useCallback((left: any, right: any) => {
    if (isCreateOption(left) || isCreateOption(right)) return isCreateOption(left) && isCreateOption(right);
    return readKey(left, valueKey) === readKey(right, valueKey);
  }, [valueKey]);

  const commitCreate = useCallback(() => {
    const text = query.trim();
    if (!text) return;
    const textKey = text.toLowerCase();
    const isDuplicate = (option: SelectValue) => readLabelText(option, labelKey).toLowerCase() === textKey;
    const selected = multi ? resolvedMulti : (resolvedSingle ? [resolvedSingle] : []);
    if (knownOptions.some(isDuplicate) || selected.some(isDuplicate)) {
      setQuery('');
      return;
    }
    const created: SelectValue = {
      label: text,
      value: text,
      [labelKey]: text,
      [valueKey]: text,
    };
    setQuery('');
    if (!multi) setOpen(false);
    if (multi) onChange([...resolvedMulti, created]);
    else onChange(created);
    onNewOptionClick?.(created);
  }, [knownOptions, labelKey, multi, onChange, onNewOptionClick, query, resolvedMulti, resolvedSingle, valueKey]);

  const handleCustomSelect = useCallback((selected: any) => {
    ignoreValueChangeUntil.current = Date.now() + 50;
    if (isCreateOption(selected)) {
      commitCreate();
      return;
    }
    if (!multi) {
      onChange(selected);
      return;
    }
    const key = readKey(selected, valueKey);
    const exists = resolvedMulti.some((item) => readKey(item, valueKey) === key);
    onChange(exists
      ? resolvedMulti.filter((item) => readKey(item, valueKey) !== key)
      : [...resolvedMulti, selected]);
  }, [commitCreate, multi, onChange, resolvedMulti, valueKey]);

  const handleValueChange = useCallback((next: any) => {
    if (Date.now() < ignoreValueChangeUntil.current) return;
    if (isCreateOption(next)) {
      commitCreate();
      return;
    }
    if (Array.isArray(next) && next.some(isCreateOption)) {
      commitCreate();
      return;
    }
    onChange(next);
  }, [commitCreate, onChange]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    setQueryDirty(false);
    if (nextOpen) {
      const dialog = hostRef.current?.closest('[role="dialog"], .modal');
      setPortalContainer(dialog instanceof HTMLElement ? dialog : undefined);
      onOpen?.();
      return;
    }
    setQuery('');
    onClose?.();
  }, [onClose, onOpen]);

  const handleInputValueChange = useCallback((next: string) => {
    const overridden = onInputChange?.(next);
    const nextValue = typeof overridden === 'string' ? overridden : next;
    const selectedText = resolvedSingle ? readLabelText(resolvedSingle, labelKey) : '';
    setQueryDirty(nextValue !== selectedText);
    setQuery(nextValue);
  }, [labelKey, onInputChange, resolvedSingle]);

  const itemToStringLabel = useCallback((item: SelectValue) => {
    if (item?.__create) return query.trim();
    return readLabelText(item, labelKey);
  }, [labelKey, query]);

  const summary = resolvedMulti.length
    ? `${readLabelText(resolvedMulti[0], labelKey)}${resolvedMulti.length > 1 ? ` + ${resolvedMulti.length - 1}` : ''}`
    : placeholderNode;

  return (
    <ComboboxPrimitive.Root
      items={items}
      filteredItems={filteredItems}
      multiple={multi as true | undefined}
      value={(multi ? resolvedMulti : resolvedSingle) as any}
      onValueChange={handleValueChange as any}
      onInputValueChange={handleInputValueChange}
      onOpenChange={handleOpenChange}
      onItemHighlighted={setHighlighted}
      open={open}
      inputValue={query}
      disabled={disabled}
      name={name}
      filter={itemFilter}
      isItemEqualToValue={sameOption}
      itemToStringLabel={itemToStringLabel}
      autoHighlight={showQueryInput && !multi}
    >
      <div ref={hostRef} id={id} className={fullClassName} style={style}>
        <div className="Select-control tw-relative" ref={controlRef}>
          {multi && queryInputCollapsed && (
            <ComboboxPrimitive.Trigger
              className="tw-absolute tw-inset-0 tw-z-0"
              disabled={disabled}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          )}
          {multi ? (
            <ComboboxPrimitive.Chips
              className={combineClassNames(
                'Select-multi-value-wrapper tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center',
                { 'tw-pointer-events-none tw-relative tw-z-[1]': queryInputCollapsed },
              )}
            >
              {simplifyMultiValue ? (
                showQueryInput ? (
                  <ComboboxPrimitive.Trigger
                    className="tw-flex tw-h-8 tw-min-w-0 tw-flex-1 tw-items-center tw-bg-transparent tw-px-1 tw-text-left"
                    onFocus={onFocus}
                    onBlur={onBlur}
                  >
                    <span className="Select-value-label tw-truncate">{summary}</span>
                  </ComboboxPrimitive.Trigger>
                ) : (
                  <span
                    className={'Select-value-label tw-pointer-events-none tw-flex tw-h-8 tw-min-w-0'
                      + ' tw-flex-1 tw-items-center tw-truncate tw-px-1'}
                  >
                    {summary}
                  </span>
                )
              ) : resolvedMulti.map((item) => (
                <ComboboxPrimitive.Chip
                  key={String(readKey(item, valueKey))}
                  className={combineClassNames(
                    'Select-value',
                    getValueChipClassName?.(item),
                    item.className,
                  )}
                  style={item.style}
                  title={item.title || readLabelText(item, labelKey)}
                >
                  {clearPosition === 'leading' && (
                    <ComboboxPrimitive.ChipRemove className="Select-value-icon tw-pointer-events-auto" aria-hidden="true">
                      <Icon type="close" className="tw-float-none" />
                    </ComboboxPrimitive.ChipRemove>
                  )}
                  {valueRenderer ? valueRenderer(item) : item.href ? (
                    <a
                      className="Select-value-label tw-min-w-0 tw-whitespace-normal tw-break-words"
                      href={item.href}
                      rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                      target={item.target}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      {readLabelText(item, labelKey)}
                    </a>
                  ) : (
                    <span className="Select-value-label tw-min-w-0 tw-whitespace-normal tw-break-words">
                      {readLabelText(item, labelKey)}
                    </span>
                  )}
                  {clearPosition === 'trailing' && (
                    <ComboboxPrimitive.ChipRemove className="Select-value-icon tw-pointer-events-auto" aria-hidden="true">
                      <Icon type="close" className="tw-float-none" />
                    </ComboboxPrimitive.ChipRemove>
                  )}
                </ComboboxPrimitive.Chip>
              ))}
              {showQueryInput && (
                <ComboboxPrimitive.Input
                  className={combineClassNames(
                    'tw-min-w-0 tw-bg-transparent tw-outline-none placeholder:tw-text-muted-foreground',
                    hasValue ? 'tw-px-0' : 'tw-pl-3 tw-pr-0',
                    {
                      'tw-absolute tw-h-0 tw-w-0 tw-overflow-hidden tw-p-0': hasValue && !open,
                      'tw-h-6 tw-w-0 tw-flex-1 tw-py-0 tw-leading-4': hasValue && open,
                      'tw-h-full tw-w-0 tw-flex-1': !hasValue,
                    },
                  )}
                  placeholder={hasValue ? '' : placeholderText}
                  disabled={disabled}
                  autoFocus={autoFocus}
                  onFocus={onFocus}
                  onBlur={onBlur}
                />
              )}
              {!showQueryInput && !simplifyMultiValue && !hasValue && (
                <span className="Select-placeholder tw-pointer-events-none tw-px-4">{placeholderNode}</span>
              )}
            </ComboboxPrimitive.Chips>
          ) : (
            <div className="tw-relative tw-flex tw-h-9 tw-w-full tw-items-center">
              {showQueryInput ? (
                <>
                  {searchable && !resolvedSingle && !open && <Icon type="search" size={18} className="tw-ml-2 tw-shrink-0" />}
                  <div className="Select-input tw-relative tw-flex tw-h-full tw-min-w-0 tw-flex-1 tw-items-center">
                    <ComboboxPrimitive.Input
                      className={combineClassNames(
                        'tw-h-full tw-min-w-0 tw-w-full tw-flex-1 tw-bg-transparent tw-px-4 tw-outline-none',
                        open ? 'tw-cursor-text' : 'tw-cursor-pointer',
                        showSelectedFace ? 'Select-input-mask tw-text-transparent tw-caret-transparent' : undefined,
                      )}
                      placeholder={showSelectedFace ? '' : placeholderText}
                      disabled={disabled}
                      autoFocus={autoFocus}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      aria-invalid={!!error || undefined}
                    />
                    {showSelectedFace && (
                      <span
                        className={'tw-pointer-events-none tw-absolute tw-inset-0 tw-flex'
                          + ' tw-items-center tw-overflow-hidden tw-px-4'}
                      >
                        {resolvedSingle && hasCustomValue ? (
                          ValueComponent
                            ? <ValueComponent value={resolvedSingle} />
                            : valueRenderer?.(resolvedSingle)
                        ) : (
                          <span className="Select-value tw-min-w-0">
                            <span className="Select-value-label tw-block tw-truncate">
                              {readLabelNode(resolvedSingle, labelKey)}
                            </span>
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <ComboboxPrimitive.Trigger
                  className="tw-flex tw-h-full tw-min-w-0 tw-w-full tw-items-center tw-bg-transparent tw-text-left"
                  disabled={disabled}
                  autoFocus={autoFocus}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  aria-invalid={!!error || undefined}
                >
                  <span
                    className={combineClassNames(
                      'tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-overflow-hidden tw-px-4',
                      { 'tw-pr-6': clearable && hasValue },
                    )}
                  >
                    {resolvedSingle && hasCustomValue ? (
                      ValueComponent
                        ? <ValueComponent value={resolvedSingle} />
                        : valueRenderer?.(resolvedSingle)
                    ) : resolvedSingle ? (
                      <span className="Select-value tw-min-w-0">
                        <span className="Select-value-label tw-block tw-truncate">
                          {readLabelNode(resolvedSingle, labelKey)}
                        </span>
                      </span>
                    ) : (
                      <span className="Select-placeholder tw-truncate">{placeholderNode}</span>
                    )}
                  </span>
                  {!hideArrow && (
                    <span className="Select-arrow-zone">
                      <span className="Select-arrow" />
                    </span>
                  )}
                </ComboboxPrimitive.Trigger>
              )}
              {clearable && hasValue && (
                <ComboboxPrimitive.Clear
                  className={combineClassNames(
                    'Select-clear-zone Select-clear',
                    {
                      'tw-absolute tw-inset-y-0': !showQueryInput,
                      'tw-right-0': !showQueryInput && hideArrow,
                      'tw-right-7': !showQueryInput && !hideArrow,
                    },
                  )}
                >
                  ×
                </ComboboxPrimitive.Clear>
              )}
              {showQueryInput && !hideArrow && (
                <ComboboxPrimitive.Trigger className="Select-arrow-zone" tabIndex={-1}>
                  <span className="Select-arrow" />
                </ComboboxPrimitive.Trigger>
              )}
            </div>
          )}
          {multi && clearable && hasValue && (
            <ComboboxPrimitive.Clear className="Select-clear-zone Select-clear tw-relative tw-z-[1]">
              ×
            </ComboboxPrimitive.Clear>
          )}
          {multi && !hideArrow && (
            queryInputCollapsed ? (
              <span className="Select-arrow-zone tw-pointer-events-none tw-relative tw-z-[1]">
                <span className="Select-arrow" />
              </span>
            ) : (
              <ComboboxPrimitive.Trigger className="Select-arrow-zone" tabIndex={-1}>
                <span className="Select-arrow" />
              </ComboboxPrimitive.Trigger>
            )
          )}
        </div>
        <ComboboxContent
          container={portalContainer}
          anchor={controlRef}
          sideOffset={-1}
          collisionBoundary={portalContainer ?? 'clipping-ancestors'}
          collisionAvoidance={{ side: 'flip', align: 'shift', fallbackAxisSide: 'end' }}
          className="Select-menu-outer"
        >
          <ComboboxEmpty className="Select-noresults">
            {(isFetching || isLoading) ? loadingText : t('common:select_no_results')}
          </ComboboxEmpty>
          <ComboboxList className="Select-menu">
            {(item: SelectValue) => {
              const selected = !multi && sameOption(resolvedSingle, item);
              const focused = highlighted != null && sameOption(highlighted, item);
              return (
                <ComboboxItem
                  key={item.__create ? '__create' : String(readKey(item, valueKey))}
                  value={item}
                  disabled={!!item.disabled}
                  className={OptionRenderer
                    ? '!tw-p-0 !tw-pr-0 [&_[data-slot=combobox-item-indicator]]:tw-hidden'
                    : combineClassNames('Select-option', {
                      'is-selected': selected,
                      'is-focused': focused,
                    })}
                >
                  {OptionRenderer ? (
                    <OptionRenderer
                      option={item}
                      isSelected={selected}
                      isFocused={focused}
                      isDisabled={!!item.disabled}
                      onSelect={handleCustomSelect}
                    />
                  ) : optionRenderer ? optionRenderer(item) : (
                    readLabelNode(item, labelKey)
                  )}
                </ComboboxItem>
              );
            }}
          </ComboboxList>
        </ComboboxContent>
        <Message error={error} warning={warning} />
      </div>
    </ComboboxPrimitive.Root>
  );
}

export function Select(props: SelectProps) {
  return <SelectControl {...props} />;
}

export function AsyncSelect(props: SelectProps) {
  return <SelectControl {...props} searchable autoload={false} />;
}

export function CreatableSelect(props: SelectProps) {
  return <SelectControl {...props} searchable creatable />;
}

export default Select;
